// How a leave type is approved — ONE three-way choice on screen, two columns underneath.
//
// LAUNCH-CHECKLIST H9 (agreed with Rob 12–13/10/2026). "Requires approval" used to be a tick
// box; it became:
//
//   always  — every request goes to the approver                 requires_approval ✓  auto_approve ✗
//   auto    — approved straight away unless it breaks a rule     requires_approval ✓  auto_approve ✓
//   never   — recorded, not asked (like sick leave)              requires_approval ✗  auto_approve ✗
//
// ⚠️ The fourth combination (never + auto) means nothing, and the backend stores it as
// "never". `flagsFor` can't produce it, so the screen can't send it.
//
// `rulesFor` is the panel Rob asked for: pick "automatically" and every rule it will check
// is listed underneath, with its current value — and it says plainly when nothing is set,
// because then every request would go straight through. Pinned by approvalMode.checks.mjs.

export const ALWAYS = 'always';
export const AUTO = 'auto';
export const NEVER = 'never';

export function modeOf(leaveType) {
  if (!leaveType) return ALWAYS;
  if (leaveType.requires_approval === false) return NEVER;
  return leaveType.auto_approve ? AUTO : ALWAYS;
}

export function flagsFor(mode) {
  if (mode === NEVER) return { requires_approval: false, auto_approve: false };
  if (mode === AUTO) return { requires_approval: true, auto_approve: true };
  return { requires_approval: true, auto_approve: false };
}

/** The badge on the leave types list. */
export function badgeFor(leaveType) {
  const mode = modeOf(leaveType);
  if (mode === NEVER) return { label: 'Recorded, not asked', tone: 'recorded' };
  if (mode === AUTO) return { label: 'Automatic within the rules', tone: 'auto' };
  return { label: 'Needs approval', tone: 'approval' };
}

export function days(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  const tidy = Number.isInteger(v) ? String(v) : String(Number(v.toFixed(2)));
  return `${tidy} ${v === 1 ? 'day' : 'days'}`;
}

function positive(n) {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/**
 * Every rule automatic approval checks for this leave type, as the panel shows them.
 *
 *   leaveType   — the form as it stands: name, include_max_off, limit_days
 *   limits      — Settings → Leave limits ([{label, max_off}])
 *   rules       — { min_notice_days, max_consecutive_days, max_advance_booking_months }
 *   managers    — { mgr_can_approve_leave, mgr_max_approval_days }
 *   blackouts   — [{ name, is_active }]
 *
 * Returns { checks, refuses, nothingChecked }. Each check is
 * { key, title, value, applies, tab } — `tab` is the Settings tab where it is changed.
 * ⚠️ Mirrors services/auto_approval.py: if a rule is added there, add it here, or the
 * panel tells an admin a request is safe when it isn't checked the way they think.
 */
export function rulesFor({ leaveType = {}, limits = [], rules = {}, managers = {}, blackouts = [] } = {}) {
  const name = (leaveType.name || '').trim() || 'this leave type';
  const counts = leaveType.include_max_off !== false;
  const live = (limits || []).filter((l) => l && Number(l.max_off) >= 0);

  const people = {
    key: 'people_off',
    title: 'People off at once',
    tab: 'limits',
    applies: counts && live.length > 0,
    value: !counts
      ? `Doesn't apply: ${name} isn't counted as being off`
      : live.length
        ? live.map((l) => `${l.label || 'Limit'}, ${Number(l.max_off)} at a time`).join(' · ')
        : 'Not set',
  };
  const notice = positive(rules.min_notice_days);
  const longest = positive(rules.max_consecutive_days);
  const yearly = positive(leaveType.limit_days);
  const mgrMax = managers.mgr_can_approve_leave === false ? 0 : positive(managers.mgr_max_approval_days);

  const checks = [
    people,
    { key: 'notice', title: 'Minimum notice', tab: 'leave', applies: notice > 0,
      value: notice ? days(notice) : 'Not set' },
    { key: 'longest', title: 'Maximum days in one booking', tab: 'leave', applies: longest > 0,
      value: longest ? days(longest) : 'Not set' },
    { key: 'yearly', title: `Yearly limit for ${name}`, tab: null, applies: yearly > 0,
      value: yearly ? days(yearly) : 'None set (the box above)' },
    { key: 'managers', title: 'What a manager may approve', tab: 'managers', applies: mgrMax > 0,
      value: mgrMax ? `Managers can approve up to ${days(mgrMax)}` : 'No limit' },
  ];

  const active = (blackouts || []).filter((b) => b && b.is_active !== false);
  const ahead = positive(rules.max_advance_booking_months);
  const refuses = [
    { key: 'blackouts', title: 'No-leave dates', tab: 'blackouts', applies: active.length > 0,
      value: active.length
        ? `${active.length} set: ${active.slice(0, 3).map((b) => b.name).join(', ')}${active.length > 3 ? '…' : ''}`
        : 'None set' },
    { key: 'basics', title: ahead
        ? `Not enough allowance, already off that day, or more than ${ahead} month${ahead === 1 ? '' : 's'} ahead`
        : 'Not enough allowance, or already off that day',
      tab: null, applies: true, value: '' },
  ];

  return { checks, refuses, nothingChecked: !checks.some((c) => c.applies) };
}
